import Link from "next/link";

import { useCallback, useMemo } from "react";

import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";

import AccountUpgradeModal from "components/AccountUpgradeModal";
import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { PermissionKey } from "components/ToolsPageLayout/ToolsPageTabs";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";
import highlightText from "utils/highLightText";

const ToolSnippet = ({
  tool,
  className,
  query,
}: {
  tool: any;
  className?: string;
  query?: string;
}) => {
  const { account } = useAuthentication();
  const { trackActivity } = useActivityTracker();
  const { toggleModal } = useSignInSignUp();
  const [showStripeModal, toggleStripeModal] = useToggle();

  const isAvailable = useMemo(() => {
    if (account) {
      return tool?.access?.some(
        (access: any) => account[access as PermissionKey]
      );
    }

    return tool.public;
  }, [account, tool.access, tool.public]);

  const shouldHide = useMemo(() => {
    if (account) {
      if (account.isPreviewPass) {
        return false;
      }
      return tool.hideOnRestricted && !isAvailable;
    }

    return false;
  }, [account, isAvailable, tool.hideOnRestricted]);

  const link = useMemo(() => {
    if (!isAvailable) return "#";

    return (tool.href || tool.link)!;
  }, [isAvailable, tool.href, tool.link]);

  const onClick = useCallback(
    async (event: any) => {
      if (tool.activityKey) {
        const toolEntity = await baseFetcher(`/api/v2/tools/${tool.id}`);
        if (toolEntity) {
          trackActivity(tool.activityKey, toolEntity, {
            name: tool.title,
            slug: `/${toolEntity.id}`,
          });
        }
      }

      if (!isAvailable && !tool.paidOnly) {
        event.preventDefault();
        toggleModal("sign-up");
      } else if (!isAvailable && tool.paidOnly) {
        event.preventDefault();
        toggleStripeModal();
      } else if (tool.withBrowserNavigation) {
        event.preventDefault();
        location.href = link;
      }
    },
    [
      isAvailable,
      link,
      toggleModal,
      toggleStripeModal,
      tool.activityKey,
      tool.id,
      tool.paidOnly,
      tool.title,
      tool.withBrowserNavigation,
      trackActivity,
    ]
  );

  if (shouldHide) return null;

  return (
    <>
      <Link
        href={link}
        key={tool.id}
        className="basis-full grow lg:grow-0 lg:basis-[calc(50%-15px)]"
        rel={tool.rel}
        scroll={false}
        target={tool.target}
        onClick={onClick}
      >
        {/* This is to fix issue with override the default fill color for SVG icons with tailwind nơt working */}
        <div className="[&>g]:!fill-white"></div>
        <CustomCard
          className={twMerge(
            "flex items-center",
            "mb-0 p-[15px] rounded-[15px] w-[100%] hover:text-black hover:bg-light-gray/40 shadow-[0_2px_15px_rgba(0,0,0,0.15)]",
            className
          )}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between flex-1">
            {!isAvailable && (
              <div className="flex md:hidden flex-1 justify-end">
                <div
                  className={twMerge(
                    "gap-[3px] flex md:hidden items-center p-[3px] px-[6px] mb-[10px] bg-inactive-gray rounded-full text-[8px]",
                    tool.paidOnly && "bg-mellow-yellow/50"
                  )}
                >
                  <FontAwesomeIcon icon={faLock} className="text-[8px]" />
                  <span>
                    {tool.paidOnly ? "Paid Plans Only" : "Sign Up to Use"}
                  </span>
                </div>
              </div>
            )}
            <div
              className={twMerge(
                "flex flex-1 items-center max-w-none",
                !isAvailable && "md:max-w-[70%]"
              )}
            >
              <div
                className={twMerge(
                  "bg-glacial-blue",
                  tool.className,
                  "min-w-[55px] min-h-[55px] rounded-[5px]",
                  "text-white text-[20px]",
                  "flex items-center justify-center",
                  "relative overflow-hidden",
                  "mr-[10px] shadow-[0_2px_15px_rgba(0,0,0,0.15)]"
                )}
              >
                {tool.icon}
                {tool.isBeta && (
                  <div
                    className={twMerge(
                      "bg-bearish-red",
                      "text-[12px] leading-[1]",
                      "py-[2px] px-[20px]",
                      "absolute top-[5px] right-[-22px]",
                      "rotate-45"
                    )}
                  >
                    BETA
                  </div>
                )}
                {tool.isNew && (
                  <div
                    className={twMerge(
                      "bg-ink-pink",
                      "text-[12px] leading-[1]",
                      "py-[2px] px-[20px]",
                      "absolute top-[5px] right-[-22px]",
                      "rotate-45"
                    )}
                  >
                    NEW
                  </div>
                )}
              </div>
              <div className="">
                <div className="font-bold text-[16px]">
                  {query ? highlightText(tool.title, query) : tool.title}
                </div>
                <div className="text-gray-gray text-[13px] line-clamp-2">
                  {query
                    ? highlightText(tool.description, query)
                    : tool.description}
                </div>
              </div>
            </div>
            {!isAvailable && (
              <div
                className={twMerge(
                  "gap-[5px] hidden md:flex items-center p-[8px] px-[16px] ml-[10px] bg-inactive-gray rounded-full text-[10px]",
                  tool.paidOnly && "bg-mellow-yellow/50"
                )}
              >
                <FontAwesomeIcon icon={faLock} className="text-[14px]" />
                <span>
                  {tool.paidOnly ? "Paid Plans Only" : "Sign Up to Use"}
                </span>
              </div>
            )}
          </div>
        </CustomCard>
      </Link>
      {showStripeModal && <AccountUpgradeModal onClose={toggleStripeModal} />}
    </>
  );
};

export default ToolSnippet;
